import { LAST_EVENT_ID_TTL_MS } from '@/constants/socketTimings';
import { isRecord } from '@/utils/isRecord';

export const LAST_EVENT_ID_KEY = 'mercureLastEventId';

interface IStoredLastEventId {
  id: string;
  savedAt: number;
}

const isStoredLastEventId = (value: unknown): value is IStoredLastEventId =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.savedAt === 'number';

/**
 * Сохраняет последний Mercure event id вместе с меткой времени.
 * Метка нужна, чтобы при старте не подставлять слишком старый id
 * и не получать от хаба большую пачку устаревших событий.
 *
 * @param id - значение поля `id:` последнего события Mercure
 */
export const saveLastEventId = (id: string): void => {
  const payload: IStoredLastEventId = { id, savedAt: Date.now() };
  localStorage.setItem(LAST_EVENT_ID_KEY, JSON.stringify(payload));
};

/**
 * Возвращает сохранённый Mercure event id, если он не старше TTL.
 * Иначе возвращает `null` (и при просрочке удаляет запись).
 *
 * @returns актуальный event id или `null`
 */
export const readLastEventId = (): string | null => {
  const raw = localStorage.getItem(LAST_EVENT_ID_KEY);

  if (raw === null) {
    return null;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    clearLastEventId();
    return null;
  }

  if (!isStoredLastEventId(parsed)) {
    clearLastEventId();
    return null;
  }

  if (Date.now() - parsed.savedAt > LAST_EVENT_ID_TTL_MS) {
    clearLastEventId();
    return null;
  }

  return parsed.id;
};

/**
 * Удаляет сохранённый Mercure event id. Вызывается при логауте.
 */
export const clearLastEventId = (): void => {
  localStorage.removeItem(LAST_EVENT_ID_KEY);
};
